import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    Box,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Stack,
    Chip,
    Avatar,
    Alert,
    LinearProgress
} from '@mui/material';
import {
    PhotoCamera,
    SmartToy,
    Feedback,
    Person,
    ThumbUp,
    ThumbDown
} from '@mui/icons-material';
import { adminFeedbackAPI } from '../../../api/adminFeedback';

const FeedbackDetailsDialog = ({
    open,
    onClose,
    selectedFeedback,
    onResolveClick,
    formatDate,
    getStatusIcon,
    getStatusColor,
    getSeverityColor
}) => {
    // Helper function to get image URL similar to FeedbackDialog
    const getImageUrl = (feedback) => {
        if (!feedback) return null;

        // Try multiple approaches to get the image URL
        if (feedback.imageUrl) {
            return feedback.imageUrl;
        }

        if (feedback.imageFileName) {
            // Use the direct static resource path
            return `${process.env.REACT_APP_API_URL || 'http://localhost:8081'}/uploads/waste/${feedback.imageFileName}`;
        }

        if (feedback.imagePath) {
            // Extract filename from full path and use direct static resource path
            const filename = feedback.imagePath.split('/').pop();
            return `${process.env.REACT_APP_API_URL || 'http://localhost:8081'}/uploads/waste/${filename}`;
        }

        return null;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3 }
            }}
        >
            <DialogTitle sx={{ pb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h5" fontWeight={700} sx={{ color: '#2196f3' }}>
                        🔍 Detailed Feedback Analysis
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                {selectedFeedback && (
                    <Grid container spacing={3}>
                        {/* Classification Image */}
                        <Grid item xs={12} md={5}>
                            <Card elevation={3} sx={{ borderRadius: 3 }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PhotoCamera /> Classification Image
                                    </Typography>
                                    {getImageUrl(selectedFeedback) ? (
                                        <Box>
                                            <CardMedia
                                                component="img"
                                                image={getImageUrl(selectedFeedback)}
                                                alt="Classification"
                                                sx={{
                                                    borderRadius: 2,
                                                    maxHeight: 300,
                                                    objectFit: 'contain',
                                                    width: '100%',
                                                    border: '2px solid #e0e0e0'
                                                }}
                                                onLoad={(e) => {
                                                    console.log('✅ Image loaded successfully:', selectedFeedback.imageFileName || selectedFeedback.imagePath);
                                                }}
                                                onError={(e) => {
                                                    console.error('❌ Image failed to load:', selectedFeedback.imageFileName || selectedFeedback.imagePath);
                                                    console.error('URL attempted:', getImageUrl(selectedFeedback));
                                                    e.target.style.display = 'none';
                                                    e.target.nextSibling.style.display = 'flex';
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    display: 'none',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    height: 200,
                                                    bgcolor: '#f5f5f5',
                                                    borderRadius: 2,
                                                    flexDirection: 'column',
                                                    gap: 1
                                                }}
                                            >
                                                <Typography color="text.secondary" fontWeight={600}>
                                                    Image not available
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    File: {selectedFeedback.imageFileName || 'Unknown'}
                                                </Typography>
                                                <Typography variant="caption" color="error">
                                                    Check server image directory
                                                </Typography>
                                            </Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                Filename: {selectedFeedback.imageFileName || selectedFeedback.imagePath?.split('/').pop() || 'Unknown'}
                                            </Typography>
                                            <Typography variant="caption" color="primary" sx={{ display: 'block', fontWeight: 600 }}>
                                                URL: {getImageUrl(selectedFeedback)}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: 200,
                                            bgcolor: '#f5f5f5',
                                            borderRadius: 2,
                                            flexDirection: 'column',
                                            gap: 1
                                        }}>
                                            <Typography color="text.secondary" fontWeight={600}>No image available</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                No image data found for this feedback
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Detailed Information */}
                        <Grid item xs={12} md={7}>
                            <Stack spacing={3}>
                                {/* AI Classification Details */}
                                <Card elevation={2} sx={{ borderRadius: 2 }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <SmartToy /> AI Classification Details
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="text.secondary">Predicted Category (AI Original)</Typography>
                                                <Chip
                                                    label={selectedFeedback.predictedCategory || 'Unknown'}
                                                    sx={{
                                                        backgroundColor: selectedFeedback.categoryColor ? `${selectedFeedback.categoryColor}20` : '#f5f5f5',
                                                        color: selectedFeedback.categoryColor || '#666',
                                                        fontWeight: 600,
                                                        mt: 0.5
                                                    }}
                                                />
                                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                    What the AI originally thought
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="text.secondary">Confidence Level</Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                    <Typography variant="h6" fontWeight={700}>
                                                        {Math.round(selectedFeedback.confidence || 0)}%
                                                    </Typography>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={selectedFeedback.confidence || 0}
                                                        sx={{
                                                            flex: 1,
                                                            height: 8,
                                                            borderRadius: 4,
                                                            backgroundColor: '#e0e0e0',
                                                            '& .MuiLinearProgress-bar': {
                                                                backgroundColor: getSeverityColor(selectedFeedback.confidence),
                                                                borderRadius: 4
                                                            }
                                                        }}
                                                    />
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="text.secondary">AI Model Version</Typography>
                                                <Typography variant="body1" fontWeight={600}>
                                                    {selectedFeedback.aiModelVersion || 'Unknown'}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="text.secondary">Classification Date</Typography>
                                                <Typography variant="body1">
                                                    {formatDate(selectedFeedback.classifiedAt)}
                                                </Typography>
                                            </Grid>
                                            {selectedFeedback.suggestion && (
                                                <Grid item xs={12}>
                                                    <Typography variant="body2" color="text.secondary">AI Suggestion</Typography>
                                                    <Typography variant="body1" sx={{
                                                        backgroundColor: '#f8f9fa',
                                                        p: 2,
                                                        borderRadius: 1,
                                                        mt: 0.5
                                                    }}>
                                                        {selectedFeedback.suggestion}
                                                    </Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </CardContent>
                                </Card>

                                {/* User Feedback Details */}
                                <Card elevation={2} sx={{ borderRadius: 2 }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Feedback /> User Feedback
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="text.secondary">User</Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                    <Avatar sx={{ width: 24, height: 24 }}>
                                                        <Person />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {selectedFeedback.userDisplayName || 'Unknown User'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {selectedFeedback.userEmail || 'No email'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="text.secondary">Feedback Type</Typography>
                                                <Chip
                                                    icon={selectedFeedback.correct ? <ThumbUp /> : <ThumbDown />}
                                                    label={selectedFeedback.correct ? 'Positive Feedback' : 'Correction Required'}
                                                    color={selectedFeedback.correct ? 'success' : 'error'}
                                                    sx={{ mt: 0.5 }}
                                                />
                                            </Grid>
                                            {selectedFeedback.providedCategory && (
                                                <Grid item xs={6}>
                                                    <Typography variant="body2" color="text.secondary">Correct Category (User Provided)</Typography>
                                                    <Chip
                                                        label={selectedFeedback.providedCategory}
                                                        sx={{
                                                            backgroundColor: '#e3f2fd',
                                                            color: '#1976d2',
                                                            fontWeight: 600,
                                                            mt: 0.5
                                                        }}
                                                    />
                                                </Grid>
                                            )}
                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="text.secondary">Feedback Date</Typography>
                                                <Typography variant="body1">
                                                    {formatDate(selectedFeedback.createdAt)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="body2" color="text.secondary">User Message</Typography>
                                                <Typography variant="body1" sx={{
                                                    backgroundColor: '#f8f9fa',
                                                    p: 2,
                                                    borderRadius: 1,
                                                    mt: 0.5
                                                }}>
                                                    {selectedFeedback.message}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="body2" color="text.secondary">Status</Typography>
                                                <Chip
                                                    icon={getStatusIcon(selectedFeedback.status)}
                                                    label={selectedFeedback.status}
                                                    sx={{
                                                        backgroundColor: `${getStatusColor(selectedFeedback.status)}20`,
                                                        color: getStatusColor(selectedFeedback.status),
                                                        fontWeight: 600,
                                                        mt: 0.5
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>

                                {/* Training Priority Assessment */}
                                {!selectedFeedback.correct && (
                                    <Card elevation={2} sx={{ borderRadius: 2, border: `2px solid ${getSeverityColor(selectedFeedback.confidence)}40` }}>
                                        <CardContent sx={{ p: 3 }}>
                                            <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <SmartToy sx={{ color: getSeverityColor(selectedFeedback.confidence) }} />
                                                AI Training Priority
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={6}>
                                                    <Typography variant="body2" color="text.secondary">Priority Level</Typography>
                                                    <Chip
                                                        label={selectedFeedback.confidence > 90 ? 'CRITICAL' :
                                                              selectedFeedback.confidence > 70 ? 'HIGH' : 'MEDIUM'}
                                                        color={selectedFeedback.confidence > 90 ? 'error' :
                                                              selectedFeedback.confidence > 70 ? 'warning' : 'info'}
                                                        sx={{ mt: 0.5, fontWeight: 700 }}
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="body2" color="text.secondary">Severity Reason</Typography>
                                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                        {selectedFeedback.confidence > 90 ? 'High confidence but incorrect - critical for training' :
                                                         selectedFeedback.confidence > 70 ? 'Medium confidence error - important for improvement' :
                                                         'Low confidence error - standard training case'}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                            <Alert
                                                severity={selectedFeedback.confidence > 90 ? 'error' :
                                                         selectedFeedback.confidence > 70 ? 'warning' : 'info'}
                                                sx={{ mt: 2, borderRadius: 2 }}
                                            >
                                                <Typography variant="body2">
                                                    <strong>AI Training Impact:</strong> This feedback case should be prioritized for model retraining
                                                    as it represents a {selectedFeedback.confidence > 90 ? 'critical' :
                                                                      selectedFeedback.confidence > 70 ? 'high priority' : 'standard'} learning opportunity.
                                                </Typography>
                                            </Alert>
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Admin Response (if any) */}
                                {selectedFeedback.response && (
                                    <Card elevation={2} sx={{ borderRadius: 2, backgroundColor: '#f0f7ff' }}>
                                        <CardContent sx={{ p: 3 }}>
                                            <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: '#1976d2' }}>
                                                📝 Admin Response
                                            </Typography>
                                            <Typography variant="body1" sx={{
                                                backgroundColor: 'white',
                                                p: 2,
                                                borderRadius: 1,
                                                border: '1px solid #e3f2fd'
                                            }}>
                                                {selectedFeedback.response}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 1 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{ borderRadius: 2 }}
                >
                    Close
                </Button>
                {selectedFeedback && selectedFeedback.status !== 'RESOLVED' && (
                    <Button
                        onClick={onResolveClick}
                        variant="contained"
                        sx={{
                            background: 'linear-gradient(45deg, #4caf50, #81c784)',
                            fontWeight: 600,
                            borderRadius: 2
                        }}
                    >
                        Resolve This Feedback
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default FeedbackDetailsDialog;
